import React, { useMemo, useCallback, useEffect, useRef } from 'react';
import { createRoot } from 'react-dom/client';
import { GeoJSON } from 'react-leaflet';
import L from 'leaflet';
import { useData } from '../../contexts/DataContext';
import { useSelection } from '../../contexts/SelectionContext';
import { useDisplayMode } from '../../contexts/DisplayModeContext';
import { useTripsData } from '../../hooks/useTripsData';
import { getColorForPercentage, defaultZoneStyle } from '../../utils/colorScale';

const ZoneLayer = ({ viewId = 1 }) => {
  const { geojson, loading } = useData();
  const { getViewSelection } = useSelection();
  const viewSelection = getViewSelection(viewId);
  const { origin, destination, setOrigin, setDestination } = viewSelection;
  const { zonePercentageMap, totalTrips, maxPercentage, hasSelection, hasValidTimeSelection, dayType, timeSlot } = useTripsData(viewId);
  const { showPercentages } = useDisplayMode();
  const geoJsonRef = useRef(null);
  const popupRootsRef = useRef(new Map());

  // Store current state in refs for popup access
  const stateRef = useRef({ origin, destination, zonePercentageMap, totalTrips, maxPercentage, hasSelection, hasValidTimeSelection, showPercentages });
  useEffect(() => {
    stateRef.current = { origin, destination, zonePercentageMap, totalTrips, maxPercentage, hasSelection, hasValidTimeSelection, showPercentages };
  }, [origin, destination, zonePercentageMap, totalTrips, maxPercentage, hasSelection, hasValidTimeSelection, showPercentages]);

  // Create a unique key that changes when selection or data changes
  const layerKey = useMemo(() => {
    const dataHash = Array.from(zonePercentageMap.entries()).map(([k, v]) => `${k}:${v?.originPercentage || 0}`).join(',');
    return `geojson-${viewId}-${origin || 'none'}-${destination || 'none'}-${dayType}-${timeSlot}-${totalTrips}-${dataHash}-${showPercentages}`;
  }, [viewId, origin, destination, dayType, timeSlot, totalTrips, zonePercentageMap, showPercentages]);

  // Get zone style based on percentage data
  const getZoneStyleFromPercentage = useCallback((zoneName, zoneData, isOrigin, isDestination, origin, destination) => {
    // Origin zone - gray with green border, no color intensity
    if (isOrigin) {
      return {
        ...defaultZoneStyle,
        color: '#2E7D32',
        weight: 3,
      };
    }

    // Destination zone (when no origin selected) - gray with blue border, no color intensity
    if (isDestination && !origin) {
      return {
        ...defaultZoneStyle,
        color: '#1565C0',
        weight: 3,
      };
    }

    // Destination zone (when origin is also selected) - colored based on percentage with blue border
    if (isDestination && zoneData) {
      const percent = zoneData.originPercentage || 0;
      return {
        fillColor: getColorForPercentage(percent),
        color: '#1565C0',
        weight: 3,
        opacity: 1,
        fillOpacity: 0.3,
      };
    }

    // Zone with data and non-zero percentage
    if (zoneData) {
      const percent = zoneData.originPercentage || 0;
      // If percentage is 0, treat as no data (gray)
      if (percent === 0) {
        return {
          fillColor: '#808080',
          color: '#606060',
          weight: 1,
          opacity: 1,
          fillOpacity: 0.25,
        };
      }
      return {
        fillColor: getColorForPercentage(percent),
        color: '#404040',
        weight: 2,
        opacity: 1,
        fillOpacity: 0.3,
      };
    }

    // Zones without data - display in gray with visible opacity
    return {
      fillColor: '#808080',
      color: '#606060',
      weight: 1,
      opacity: 1,
      fillOpacity: 0.25,
    };
  }, []);

  // Style function for each feature
  const style = useCallback((feature) => {
    const zoneName = feature.properties?.name;
    if (!hasSelection || !hasValidTimeSelection) {
      return defaultZoneStyle;
    }
    const isOriginZone = zoneName === origin;
    const isDestinationZone = zoneName === destination;
    const zoneData = zonePercentageMap.get(zoneName);
    return getZoneStyleFromPercentage(zoneName, zoneData, isOriginZone, isDestinationZone, origin, destination);
  }, [hasSelection, hasValidTimeSelection, origin, destination, zonePercentageMap, getZoneStyleFromPercentage]);

  // Handle each feature for hover and popup
  const onEachFeature = useCallback((feature, layer) => {
    const zoneName = feature.properties?.name;

    // Get current zone data
    const zoneData = zonePercentageMap.get(zoneName);
    const percent = zoneData?.originPercentage || 0;
    const trips = zoneData?.trips || 0;

    // Create popup with React content
    const popupContainer = document.createElement('div');
    popupContainer.className = 'zone-popup-container';

    const popup = L.popup({
      className: 'zone-popup',
      closeButton: true,
      autoClose: true,
    }).setContent(popupContainer);

    layer.bindPopup(popup);

    // Render React component into popup when opened - uses current state from ref
    layer.on('popupopen', () => {
      const currentState = stateRef.current;
      const currentZoneData = currentState.zonePercentageMap.get(zoneName);
      const currentPercent = currentZoneData?.originPercentage || 0;
      const currentTrips = currentZoneData?.trips || 0;

      const root = createRoot(popupContainer);
      popupRootsRef.current.set(zoneName, root);
      root.render(
        <ZoneTooltipWrapper
          zoneName={zoneName}
          trips={currentTrips}
          percent={currentPercent}
          origin={currentState.origin}
          destination={currentState.destination}
          setOrigin={setOrigin}
          setDestination={setDestination}
          viewId={viewId}
        />
      );
    });

    layer.on('popupclose', () => {
      const root = popupRootsRef.current.get(zoneName);
      if (root) {
        setTimeout(() => {
          root.unmount();
          popupRootsRef.current.delete(zoneName);
        }, 0);
      }
    });

    // Bind percentage/name tooltip - controlled by display mode
    // Use closure-scoped values (origin, destination, etc.) here because
    // onEachFeature runs synchronously during GeoJSON mount, BEFORE useEffect
    // updates stateRef. Using stateRef here would give stale values from the
    // previous render, causing wrong labels after switching origin/destination.
    const isOriginZone = zoneName === origin;
    const isDestinationZone = zoneName === destination;
    
    // Show label for zones with data (percentages mode) or all zones (trips mode)
    const showLabel = hasSelection && hasValidTimeSelection && !isOriginZone && !isDestinationZone;
    
    if (showLabel) {
      let labelText = '';
      if (showPercentages) {
        // Show percentage only if there's data and percentage > 0
        if (percent > 0) {
          labelText = `${percent.toFixed(2)}%`;
        }
      } else {
        // Show trips value (formatted with commas for readability)
        if (trips > 0) {
          labelText = trips.toLocaleString('it-IT');
        }
      }
      
      if (labelText) {
        layer.bindTooltip(labelText, {
          permanent: true,
          direction: 'center',
          className: showPercentages ? 'zone-percentage-label' : 'zone-trips-label',
        });
      }
    }

    // Hover effects
    layer.on({
      mouseover: (e) => {
        const targetLayer = e.target;
        const currentState = stateRef.current;
        const currentZoneData = currentState.zonePercentageMap.get(zoneName);
        const isOrigin = zoneName === currentState.origin;
        const isDest = zoneName === currentState.destination;
        const baseStyle = getZoneStyleFromPercentage(zoneName, currentZoneData, isOrigin, isDest, currentState.origin, currentState.destination);

        if (currentState.hasSelection && currentState.hasValidTimeSelection) {
          targetLayer.setStyle({
            ...baseStyle,
            weight: baseStyle.weight + 1,
            fillOpacity: Math.min(0.5, baseStyle.fillOpacity + 0.15),
          });
        } else {
          targetLayer.setStyle({
            ...defaultZoneStyle,
            weight: defaultZoneStyle.weight + 1,
            fillOpacity: Math.min(0.3, defaultZoneStyle.fillOpacity + 0.15),
          });
        }
        targetLayer.bringToFront();
      },
      mouseout: (e) => {
        const targetLayer = e.target;
        const currentState = stateRef.current;
        const currentZoneData = currentState.zonePercentageMap.get(zoneName);
        const isOrigin = zoneName === currentState.origin;
        const isDest = zoneName === currentState.destination;

        if (currentState.hasSelection && currentState.hasValidTimeSelection) {
          targetLayer.setStyle(getZoneStyleFromPercentage(zoneName, currentZoneData, isOrigin, isDest, currentState.origin, currentState.destination));
        } else {
          targetLayer.setStyle(defaultZoneStyle);
        }
      },
    });
  }, [viewId, zonePercentageMap, origin, destination, hasSelection, hasValidTimeSelection, setOrigin, setDestination, getZoneStyleFromPercentage, showPercentages]);

  // Cleanup popup roots on unmount
  useEffect(() => {
    const rootsMap = popupRootsRef.current;
    return () => {
      rootsMap.forEach((root) => {
        try {
          root.unmount();
        } catch (e) {
          // Ignore unmount errors
        }
      });
      rootsMap.clear();
    };
  }, []);

  if (loading || !geojson) {
    return null;
  }

  return (
    <GeoJSON
      ref={geoJsonRef}
      key={layerKey}
      data={geojson}
      style={style}
      onEachFeature={onEachFeature}
    />
  );
};

// Wrapper component for the popup content
const ZoneTooltipWrapper = ({ zoneName, trips, percent, origin, destination, setOrigin, setDestination }) => {
  const isCurrentOrigin = origin === zoneName;
  const isCurrentDestination = destination === zoneName;

  const handleSetOrigin = () => {
    if (isCurrentOrigin) {
      setOrigin(null);
    } else {
      // Clear any other selected city (both origin and destination)
      setOrigin(zoneName);
      setDestination(null);
    }
  };

  const handleSetDestination = () => {
    if (isCurrentDestination) {
      setDestination(null);
    } else {
      // Clear any other selected city (both origin and destination)
      setDestination(zoneName);
      setOrigin(null);
    }
  };

  // Generate descriptive text based on selection state
  const getDescriptiveText = () => {
    if (!origin && !destination) {
      return null;
    }
    if (origin && !isCurrentOrigin) {
      // Only origin selected and this is not the origin: trips that ended in this region
      return `${percent.toFixed(2)}% di tutti i viaggi partiti da ${origin} sono terminati in questa zona`;
    }
    if (destination && !isCurrentDestination) {
      // Only destination selected and this is not the destination (it is an origin)
      return `${percent.toFixed(2)}% di tutti i viaggi arrivati a ${destination} sono partiti da questa zona`;
    }
    return null;
  };

  const descriptiveText = getDescriptiveText();

  return (
    <div className="zone-tooltip" style={{ width: '100%' }}>
      <div style={{ fontWeight: 'bold', fontSize: 14, marginBottom: 8, wordBreak: 'break-word' }}>{zoneName}</div>

      {trips > 0 && !isCurrentOrigin && (
        <div style={{ color: '#666', fontSize: 12, marginBottom: 8 }}>
          Viaggi: {trips} ({percent.toFixed(2)}%)
        </div>
      )}

      {trips > 0 && descriptiveText && (
        <div style={{ color: '#444', fontSize: 11, marginBottom: 8, lineHeight: 1.4 }}>
          {descriptiveText}
        </div>
      )}

      {(isCurrentOrigin || isCurrentDestination) && (
        <div style={{ color: '#666', fontSize: 11, marginBottom: 8, fontStyle: 'italic' }}>
          {isCurrentOrigin && 'Attualmente impostato come Origine'}
          {isCurrentDestination && 'Attualmente impostato come Destinazione'}
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, width: '100%' }}>
        <button
          onClick={handleSetOrigin}
          style={{
            padding: '8px 16px',
            border: 'none',
            borderRadius: 4,
            cursor: 'pointer',
            backgroundColor: isCurrentOrigin ? '#4CAF50' : '#f0f0f0',
            color: isCurrentOrigin ? '#fff' : '#333',
            fontSize: 12,
            fontWeight: 500,
            width: '100%',
          }}
        >
          {isCurrentOrigin ? 'Rimuovi Origine' : 'Imposta come Origine'}
        </button>
        <button
          onClick={handleSetDestination}
          style={{
            padding: '8px 16px',
            border: 'none',
            borderRadius: 4,
            cursor: 'pointer',
            backgroundColor: isCurrentDestination ? '#2196F3' : '#f0f0f0',
            color: isCurrentDestination ? '#fff' : '#333',
            fontSize: 12,
            fontWeight: 500,
            width: '100%',
          }}
        >
          {isCurrentDestination ? 'Rimuovi Destinazione' : 'Imposta come Destinazione'}
        </button>
      </div>
    </div>
  );
};

export default ZoneLayer;
